// ═══════════════════════════════════════════════════════════
// SwipeBack — wrap a page to enable iOS-style edge-swipe back
// LTR: swipe right (positive x) triggers back
// RTL: swipe left (negative x) triggers back
// ═══════════════════════════════════════════════════════════

import { ReactNode } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useLang } from "@/lib/i18n";

interface Props {
  onBack: () => void;
  children: ReactNode;
}

const DISTANCE_THRESHOLD = 90;     // px — minimum drag to trigger back
const VELOCITY_THRESHOLD = 500;    // px/s — flick velocity bypass

export default function SwipeBack({ onBack, children }: Props) {
  const { isRtl } = useLang();
  const x = useMotionValue(0);

  // Sign convention: +1 means "back direction".
  // LTR back = swipe right (+x). RTL back = swipe left (-x).
  const sign = isRtl ? -1 : 1;

  // Subtle shadow / opacity feedback as the user drags into the back direction
  const opacity = useTransform(x, [-200 * sign, 0, 200 * sign], [0.4, 1, 1]);

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={isRtl ? { left: 0.4, right: 0 } : { left: 0, right: 0.4 }}
      dragMomentum={false}
      dragDirectionLock
      style={{ x, opacity, touchAction: "pan-y" }}
      onDragEnd={(_, info) => {
        const dist = info.offset.x * sign;
        const vel = info.velocity.x * sign;
        if (dist > DISTANCE_THRESHOLD || vel > VELOCITY_THRESHOLD) {
          onBack();
        }
      }}
    >
      {children}
    </motion.div>
  );
}
